import React, { useState } from 'react';
import { Alert, App, Button, Card, Empty, InputNumber, Select, Space, Table, Tag, Tooltip } from 'antd';
import { CloseOutlined, DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import {
  asQuantityUnit,
  buildQuantitySetFromPair,
  MAX_CUSTOMER_PHYSICAL_QUANTITY,
  MAX_CUSTOMER_PRICING_QUANTITY,
  quantitySetToFields,
} from '../../../utils/commercialQuantity';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import CurrencyAmount from '../../../components/CurrencyAmount';
import VariantPicker from '../../../components/inventory/VariantPicker';
import { getVariantOrderLinePatch } from '../../products/orderVariantSelection';
import { OrderDto } from '../dto';
import { getDisplayOrderNumber } from '../types';
import { useFeedback } from '../../../utils/feedback';
import { ORDER_CURRENCIES } from '../currencies';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';
import { OrderProductPayload, saveOrderProducts } from '../api/orderProductsApi';
import { fetchCustomerProductPrices } from '../../customers/api/customerProductPricesApi';
import { getTotalsByCurrency, OrderProductLineDraft } from '../orderProductLineTotals';
import {
  calculateSaleQuantity,
} from '../orderProductSaleQuantity';
import { formatCommercialPriceUnit } from '../commercialPriceUnit';
import CommercialQuantityEditor from '../components/CommercialQuantityEditor';
import {
  getOrderProductDeleteCargoConflictMessage,
  getOrderProductSaveErrorMessage,
} from '../orderProductSaveErrors';
import {
  omitUnusedOrderProductHeight,
} from '../orderProductDimensions';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import {
  getOrderProductDisplayDimensions,
  getOrderProductDetailDisplayName,
  getOrderProductDisplayName,
  getOrderProductVariantLabels,
} from '../orderProductDisplay';
import { useOrderFifoPreview } from '../queries/useOrderFifoPreview';
import './OrderProductsSection.css';

const stripOrderProductDisplayFields = <
  T extends { productCategoryDimensionDisplayProfile?: string | null }
>(
  product: T,
): Omit<T, 'productCategoryDimensionDisplayProfile'> => {
  const { productCategoryDimensionDisplayProfile, ...apiProduct } = product;
  return apiProduct;
};

interface EditableOrderProduct {
  id: string;
  displayName?: string;
  productVariantId?: string | null;
  productCode?: string | null;
  productEan?: string | null;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string>;
  variantIsDefault?: boolean;
  catalogProductWidth?: number;
  catalogProductHeight?: number;
  catalogProductLength?: number;
  catalogProductThickness?: number;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  name: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  saleUnit?: QuantityUnit;
  saleQuantity?: number;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  unit?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  currency?: string;
  description?: string;
  cnCode?: string;
  netWeight?: number;
  grossWeight?: number;
  countryOfOrigin?: string;
}

interface OrderProductsSectionProps {
  order: OrderDto;
  canEditProducts: boolean;
  canViewSalePrices: boolean;
  canEditSalePrices: boolean;
  canViewMargin?: boolean;
  salePricesIncomplete?: boolean;
  onSaved: () => Promise<unknown>;
}

const renderProductRevenueTotals = (
  products: readonly EditableOrderProduct[],
  fallbackCurrency: string,
  preferPersistedTotals = false,
  language = 'pl',
) => {
  if (preferPersistedTotals) {
    const totals = products.reduce<Record<string, number>>((acc, product) => {
      const amount = Number(product.totalPrice);
      if (!Number.isFinite(amount)) return acc;
      const currency = product.currency || fallbackCurrency;
      acc[currency] = (acc[currency] || 0) + amount;
      return acc;
    }, {});
    const entries = Object.entries(totals);

    if (entries.length > 0) {
      return entries
        .map(([currency, amount]) => formatCurrencyLocalized(amount, currency, language))
        .join(' / ');
    }
  }

  const totals = getTotalsByCurrency(
    products as readonly OrderProductLineDraft[],
    fallbackCurrency,
  );
  const entries = Object.entries(totals);

  if (entries.length === 0) {
    return formatCurrencyLocalized(0, fallbackCurrency, language);
  }

  return entries
    .map(([currency, amount]) => formatCurrencyLocalized(amount, currency, language))
    .join(' / ');
};

export const OrderProductsSection: React.FC<OrderProductsSectionProps> = ({
  order,
  canEditProducts,
  canViewSalePrices,
  canEditSalePrices,
  canViewMargin = false,
  salePricesIncomplete = false,
  onSaved,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common', 'products']);
  const { notification } = App.useApp();
  const feedback = useFeedback();
  const [editingProducts, setEditingProducts] = useState(false);
  const [editedProducts, setEditedProducts] = useState<EditableOrderProduct[]>([]);
  const [savingProducts, setSavingProducts] = useState(false);
  // Per-customer fixed prices keyed by variant id; powers unit-price auto-fill.
  const [customerPriceByVariant, setCustomerPriceByVariant] = useState<
    Map<string, { fixedPrice: number; currency: string }>
  >(new Map());

  const hasProductData = order.productsLoaded;
  const orderProducts = hasProductData ? order.products : [];
  const fifo = useOrderFifoPreview(order.id, editingProducts ? editedProducts : orderProducts, canViewMargin && hasProductData);
  const renderFifoValue = (product: EditableOrderProduct, margin = false) => {
    const line = fifo.lines.find(item => item.id === product.id);
    const amount = line?.purchaseUnitPrice;
    const currency = product.currency || order.currency || 'PLN';
    return <Tooltip title={fifo.failed ? t('orders:orderDetail.fifoUnavailable') : t('orders:orderDetail.fifoHint')}>
      <span className="order-products-fifo-value">
        {margin
          ? (line?.marginPercent == null ? '—' : `${line.marginPercent.toLocaleString('pl-PL')}%`)
          : (amount == null ? '—' : formatCurrencyLocalized(amount, currency, i18n.language))}
        {!margin && amount != null && <small>/ {formatCommercialPriceUnit('sale', product, i18n.language)}</small>}
        {line?.hasShortage && !margin && <small>{t('orders:orderDetail.fifoShortage')}</small>}
      </span>
    </Tooltip>;
  };
  const handleEditProducts = async () => {
    if (!hasProductData) {
      feedback.warning(t('orders:orderDetail.messages.productsUnavailable'));
      return;
    }

    const nextProducts = order.products.map((product) => ({
      ...product,
      displayName: getOrderProductDisplayName(product, product.name),
      currency: product.currency || order.currency || 'PLN',
    }));
    setEditedProducts(nextProducts);
    setEditingProducts(true);

    if (order.customerId && canViewSalePrices) {
      try {
        const prices = await fetchCustomerProductPrices(order.customerId);
        setCustomerPriceByVariant(
          new Map(
            prices.map((price) => [
              price.productVariantId,
              { fixedPrice: price.fixedPrice, currency: price.currency }
            ])
          )
        );
      } catch (error) {
        // Non-critical. Without prices, unit price stays manual.
      }
    }
  };

  const handleCancelEditProducts = () => {
    setEditingProducts(false);
    setEditedProducts([]);
  };

  const handleAddProduct = () => {
    const newProduct: EditableOrderProduct = {
      id: `new-${Date.now()}`,
      name: '',
      quantity: 1,
      saleUnit: 'pcs',
      unitPrice: 0,
      currency: order.currency || 'PLN',
      unit: 'pcs',
    };
    setEditedProducts((current) => [
      ...current,
      newProduct,
    ]);
  };

  const handleRemoveProduct = (productId: string) => {
    setEditedProducts((current) => current.filter((product) => product.id !== productId));
  };

  const handleSaveProducts = async () => {
    const hasInvalidProducts = editedProducts.some((product) => {
      const name = (product.name || '').trim();
      const quantity = Number(product.quantity);
      const unitPrice = Number(product.unitPrice ?? 0);

      return !name || !Number.isFinite(quantity) || quantity <= 0 || (canEditSalePrices && (!Number.isFinite(unitPrice) || unitPrice < 0));
    });

    if (hasInvalidProducts) {
      feedback.error(t('orders:orderDetail.messages.invalidProductData'), { sticky: true });
      return;
    }

    setSavingProducts(true);

    try {
      const payload: OrderProductPayload[] = editedProducts.map((product) => {
        const basePayload = {
          id: product.id?.startsWith('new-') ? undefined : product.id,
          productVariantId: product.productVariantId ?? null,
          name: product.name.trim(),
          quantity: product.quantity,
          unit: asQuantityUnit(product.unit),
          saleUnit: asQuantityUnit(product.saleUnit || product.unit),
          saleQuantity: product.saleQuantity ?? product.quantity,
          conversionUnit: product.conversionUnit ?? null,
          conversionQuantity: product.conversionQuantity,
          width: product.width,
          height: product.height,
          length: product.length,
          thickness: product.thickness,
          productCategoryDimensionDisplayProfile: product.productCategoryDimensionDisplayProfile,
          description: product.description,
          cnCode: product.cnCode,
          netWeight: product.netWeight,
          grossWeight: product.grossWeight,
          countryOfOrigin: product.countryOfOrigin,
        };

        if (!canEditSalePrices) {
          return stripOrderProductDisplayFields(omitUnusedOrderProductHeight(basePayload));
        }

        return stripOrderProductDisplayFields(omitUnusedOrderProductHeight({
          ...basePayload,
          unitPrice: product.unitPrice ?? 0,
          currency: product.currency || order.currency || 'PLN',
        }));
      });

      await saveOrderProducts(order.id, payload);
      feedback.success(t('orders:orderDetail.productsUpdated', {
        orderNumber: getDisplayOrderNumber(order),
      }));
      setEditingProducts(false);
      setEditedProducts([]);
      await onSaved();
    } catch (error) {
      // S32: a blocked cargo-referenced-line delete (400
      // ORDER_PRODUCT_HAS_TRANSPORT_CARGO) gets a Polish-first, route/product
      // -specific message; every other 4xx/5xx falls through to the existing
      // generic handling.
      const cargoConflictMessage = getOrderProductDeleteCargoConflictMessage(t, error);
      if (cargoConflictMessage) {
        // The backend rejected the whole replace transaction. Put every
        // persisted line back into the draft while preserving edits/new lines
        // that were not part of the failed removal.
        setEditedProducts((current) => {
          const currentById = new Map(current.map((product) => [product.id, product]));
          const restoredPersisted = order.products.map((product) => currentById.get(product.id) ?? {
            ...product,
            displayName: getOrderProductDisplayName(product, product.name),
            currency: product.currency || order.currency || 'PLN',
          });
          const newProducts = current.filter((product) => product.id.startsWith('new-'));
          return [...restoredPersisted, ...newProducts];
        });
        notification.error({
          message: t('orders:orderDetail.messages.productHasTransportCargoTitle'),
          description: cargoConflictMessage,
          duration: 8,
        });
      } else {
        feedback.error(getOrderProductSaveErrorMessage(t, error, t('orders:orderDetail.messages.productSaveFailed')));
      }
    } finally {
      setSavingProducts(false);
    }
  };

  const renderEditableProductRow = (product: EditableOrderProduct, index: number) => {
    const dimensions = getOrderProductDisplayDimensions(product);
    const displayName = getOrderProductDisplayName(product, product.name);
    const saleQuantity = calculateSaleQuantity(product) ?? 0;
    const currency = product.currency || order.currency || 'PLN';

    return (
      <div className="order-products-editor__row" key={product.id}>
        <div className="order-products-editor__field order-products-editor__field--product">
          <span className="order-products-editor__label">
            {t('orders:orderDetail.product')}
          </span>
          <div className="order-products-editor__product-identity">
            <div className="order-products-editor__product-copy">
              <Tooltip
                title={displayName || undefined}
                trigger={['hover', 'focus']}
              >
                <strong
                  className={`order-products-editor__product-name${displayName ? '' : ' is-empty'}`}
                  tabIndex={displayName ? 0 : undefined}
                  aria-label={displayName || undefined}
                >
                  {displayName || t('orders:orderDetail.variantPickerPlaceholder')}
                </strong>
              </Tooltip>
              {dimensions && (
                <span className="order-products-editor__product-dimensions">
                  {dimensions}
                </span>
              )}
            </div>
            <div className="order-products-editor__product-picker">
              <VariantPicker
                size="middle"
                value={product.productVariantId || undefined}
                unlinkedLabel={displayName}
                triggerLabel={product.productVariantId
                  ? t('orders:orderDetail.variantPickerChange')
                  : t('orders:orderDetail.variantPickerPlaceholder')}
                placeholder={t('orders:orderDetail.variantPickerPlaceholder')}
                showInventory={false}
                style={{ width: '100%' }}
                onChange={(variantId, variant) => {
                  const updated = [...editedProducts];
                  const fixedPrice = canViewSalePrices && variantId
                    ? customerPriceByVariant.get(variantId)
                    : undefined;
                  const nextProduct = omitUnusedOrderProductHeight({
                    ...updated[index],
                    ...getVariantOrderLinePatch(
                      variantId,
                      variant,
                      updated[index],
                      fixedPrice,
                    ),
                  });
                  updated[index] = {
                    ...nextProduct,
                    saleUnit: asQuantityUnit(nextProduct.unit),
                    saleQuantity: nextProduct.quantity,
                    displayName: getOrderProductDisplayName(nextProduct, nextProduct.name),
                  };
                  setEditedProducts(updated);
                  if (fixedPrice) {
                    feedback.success(t('orders:orderDetail.customerPriceApplied', {
                      productName: nextProduct.name,
                    }));
                  }
                }}
              />
            </div>
          </div>
        </div>

        {canViewMargin && <div className="order-products-editor__field order-products-editor__field--purchase">
          <span className="order-products-editor__label">{t('orders:orderDetail.fifoPurchasePrice')}</span>
          {renderFifoValue(product)}
        </div>}
        <div className="order-products-editor__field order-products-editor__field--commercial">
          <span className="order-products-editor__label">
            {t('orders:unitConversion.physicalQuantity')} / {t('orders:unitConversion.pricingQuantity')}
          </span>
          <CommercialQuantityEditor
            compact
            maxPhysicalQuantity={MAX_CUSTOMER_PHYSICAL_QUANTITY}
            maxPricingQuantity={MAX_CUSTOMER_PRICING_QUANTITY}
            width={product.width}
            length={product.length}
            height={product.height}
            thickness={product.thickness}
            physicalUnit={(product.unit || 'pcs') as QuantityUnit}
            value={{
              physicalQuantity: product.quantity,
              physicalUnit: (product.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
              pricingQuantity: product.saleQuantity ?? product.quantity,
              pricingUnit: (product.saleUnit || product.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
              conversionQuantity: product.conversionQuantity,
              conversionUnit: product.conversionUnit,
              quantityPcs: product.quantityPcs ?? null,
              quantityM2: product.quantityM2 ?? null,
              quantityM3: product.quantityM3 ?? null,
            }}
            onChange={(next) => {
              const updated = [...editedProducts];
              const nextQuantitySet = buildQuantitySetFromPair(next, product);
              updated[index] = {
                ...updated[index],
                quantity: next.physicalQuantity,
                unit: next.physicalUnit,
                saleQuantity: next.pricingQuantity,
                saleUnit: next.pricingUnit,
                conversionQuantity: next.conversionQuantity,
                conversionUnit: next.conversionUnit,
                ...(nextQuantitySet ? quantitySetToFields(nextQuantitySet) : {}),
              };
              setEditedProducts(updated);
            }}
          />
        </div>

        {canViewSalePrices && (
          <>
            <div className="order-products-editor__field order-products-editor__field--price">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.unitPriceWithSaleUnit')}
              </span>
              <InputNumber
                size="small"
                min={0}
                max={1000000}
                step={0.01}
                precision={2}
                {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                addonAfter={`/ ${formatCommercialPriceUnit('sale', product, i18n.language)}`}
                value={product.unitPrice ?? 0}
                aria-label={t('orders:orderDetail.unitPriceWithSaleUnit')}
                onChange={(value) => {
                  const updated = [...editedProducts];
                  const parsed = Number(value);
                  updated[index] = {
                    ...updated[index],
                    unitPrice: Number.isFinite(parsed) ? parsed : 0,
                  };
                  setEditedProducts(updated);
                }}
                style={{ width: '100%' }}
              />
            </div>

            <div className="order-products-editor__field order-products-editor__field--currency">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.currency')}
              </span>
              <Select
                size="small"
                value={currency}
                onChange={(value) => {
                  const updated = [...editedProducts];
                  updated[index] = {
                    ...updated[index],
                    currency: value,
                  };
                  setEditedProducts(updated);
                }}
                style={{ width: '100%' }}
                aria-label={t('orders:orderDetail.currency')}
              >
                {ORDER_CURRENCIES.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="order-products-editor__field order-products-editor__field--total">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.saleTotal')}
              </span>
              <strong className="order-products-editor__total-value">
                {formatCurrencyLocalized(saleQuantity * (product.unitPrice || 0), currency, i18n.language)}
              </strong>
            </div>
          </>
        )}

        {canViewMargin && <div className="order-products-editor__field order-products-editor__field--margin">
          <span className="order-products-editor__label">{t('orders:orderDetail.lineMargin')}</span>
          {renderFifoValue(product, true)}
        </div>}
        <div className="order-products-editor__remove">
          <Tooltip title={t('common:delete')}>
            <Button
              danger
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveProduct(product.id)}
              aria-label={t('common:delete')}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const productColumns = [
    {
      title: t('orders:orderDetail.product'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: EditableOrderProduct) => {
        if (record.productVariantId) {
          const displayName = getOrderProductDetailDisplayName(record, name);
          const variantLabels = getOrderProductVariantLabels(
            record,
            t('products:catalogPicker.basicProduct'),
          );
          return (
            <div className="order-product-identity">
              <span className="order-product-identity__name">{displayName}</span>
              <small>{getOrderProductDisplayDimensions(record)}</small>
              <Space size={[4, 4]} wrap className="order-product-identity__labels">
                {variantLabels.map((label) => (
                  <Tag
                    key={`${label.key || 'variant'}:${label.value}`}
                    color={label.isBaseProduct ? 'blue' : undefined}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {label.key ? `${label.key}: ${label.value}` : label.value}
                  </Tag>
                ))}
                <Tooltip title={t('orders:orderDetail.variantLinkedHint')}>
                  <Tag bordered={false} color="green" icon={<LinkOutlined />} style={{ marginInlineEnd: 0 }}>
                    {t('orders:orderDetail.variantLinked')}
                  </Tag>
                </Tooltip>
              </Space>
            </div>
          );
        }
        return getOrderProductDisplayName(record, name);
      },
    },
    ...(canViewMargin ? [{
      title: t('orders:orderDetail.fifoPurchasePrice'),
      key: 'fifoPurchase',
      width: 115,
      render: (_: unknown, record: EditableOrderProduct) => renderFifoValue(record),
    }] : []),
    {
      title: `${t('orders:unitConversion.physicalQuantity')} / ${t('orders:unitConversion.pricingQuantity')}`,
      key: 'commercialQuantity',
      width: 180,
      render: (_: unknown, record: EditableOrderProduct) => (
        <CommercialQuantityEditor
          compact
          disabled
          maxPhysicalQuantity={MAX_CUSTOMER_PHYSICAL_QUANTITY}
          maxPricingQuantity={MAX_CUSTOMER_PRICING_QUANTITY}
          width={record.width}
          length={record.length}
          height={record.height}
          thickness={record.thickness}
          value={{
            physicalQuantity: record.quantity,
            physicalUnit: (record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
            pricingQuantity: record.saleQuantity ?? record.quantity,
            pricingUnit: (record.saleUnit || record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
            conversionQuantity: record.conversionQuantity,
            conversionUnit: record.conversionUnit,
            quantityPcs: record.quantityPcs ?? null,
            quantityM2: record.quantityM2 ?? null,
            quantityM3: record.quantityM3 ?? null,
          }}
          onChange={() => undefined}
        />
      ),
    },
    ...(canViewSalePrices ? [
      {
        title: t('orders:orderDetail.unitPrice'),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        render: (unitPrice: number | undefined, record: EditableOrderProduct) => {
          const lineCurrency = record.currency || order.currency || 'PLN';
          const orderCurrency = order.currency || 'PLN';
          const lineRate = lineCurrency === orderCurrency ? (order.exchangeRate ?? null) : null;
          return (
            <Space size={4}>
              <CurrencyAmount
                amount={unitPrice ?? 0}
                currency={lineCurrency}
                rate={lineRate}
                rateDate={order.exchangeRateEffectiveDate || order.orderDate}
                rateSource={order.exchangeRateSource}
                rateTableNumber={order.exchangeRateTableNumber}
              />
              <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                / {formatCommercialPriceUnit('sale', record, i18n.language)}
              </span>
            </Space>
          );
        },
      },
      {
        title: t('orders:orderDetail.currency'),
        dataIndex: 'currency',
        key: 'currency',
        width: 70,
        render: (currency: string | undefined) => currency || order.currency || 'PLN',
      },
      {
        title: t('orders:orderDetail.saleTotal'),
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        render: (totalPrice: number | undefined, record: EditableOrderProduct) => {
          const lineCurrency = record.currency || order.currency || 'PLN';
          const orderCurrency = order.currency || 'PLN';
          const lineRate = lineCurrency === orderCurrency ? (order.exchangeRate ?? null) : null;
          return (
            <CurrencyAmount
              amount={totalPrice ?? 0}
              currency={lineCurrency}
              rate={lineRate}
              rateDate={order.exchangeRateEffectiveDate || order.orderDate}
              rateSource={order.exchangeRateSource}
              rateTableNumber={order.exchangeRateTableNumber}
            />
          );
        },
      },
      ...(canViewMargin ? [{
        title: t('orders:orderDetail.lineMargin'),
        key: 'fifoMargin',
        width: 76,
        render: (_: unknown, record: EditableOrderProduct) => renderFifoValue(record, true),
      }] : []),
    ] : []),
  ];

  return (
    <Card
      title={renderSectionTitle(canViewSalePrices ? t('orders:orderDetail.sales') : t('orders:orderDetail.products'))}
      style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      extra={
        canEditProducts && (
          editingProducts ? (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                loading={savingProducts}
                onClick={handleSaveProducts}
              >
                {t('orders:orderDetail.saveProducts')}
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEditProducts}
                disabled={savingProducts}
              >
                {t('orders:orderDetail.cancelEdit')}
              </Button>
            </Space>
          ) : (
            <Tooltip title={!hasProductData ? t('orders:orderDetail.messages.productsUnavailable') : undefined}>
              <span>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEditProducts}
                  disabled={!hasProductData}
                >
                  {t('orders:orderDetail.editProducts')}
                </Button>
              </span>
            </Tooltip>
          )
        )
      }
    >
      {!hasProductData && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.messages.productsUnavailable')}
          style={{ marginBottom: 16 }}
        />
      )}
      {canViewSalePrices && salePricesIncomplete && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.salePricesIncompleteTitle')}
          description={t('orders:orderDetail.salePricesIncompleteDescription')}
          action={
            canEditProducts && !editingProducts ? (
              <Button size="small" type="primary" onClick={handleEditProducts}>
                {t('orders:orderDetail.editProducts')}
              </Button>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {editingProducts ? (
        <div
          className={`order-products-editor${canViewSalePrices ? '' : ' order-products-editor--without-prices'}`}
          data-testid="order-products-editor"
        >
          <div className="order-products-editor__list">
            {editedProducts.length > 0 ? (
              editedProducts.map(renderEditableProductRow)
            ) : (
              <Empty
                className="order-products-editor__empty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('orders:orderDetail.messages.noProductsToSave')}
              />
            )}
          </div>

          {canViewSalePrices && editedProducts.length > 0 && (
            <div className="order-products-editor__summary">
              <span>{t('orders:orderDetail.total')}</span>
              <strong>
                {renderProductRevenueTotals(editedProducts, order.currency || 'PLN', false, i18n.language)}
              </strong>
            </div>
          )}
        </div>
      ) : (
        <div className="order-products-table" style={{ width: '100%', maxWidth: '100%' }}>
          <Table
            dataSource={orderProducts}
            columns={productColumns}
            rowKey="id"
            size="middle"
            pagination={false}
            tableLayout="fixed"
            summary={canViewSalePrices ? (data) => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>{t('orders:orderDetail.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={canViewMargin ? 4 : 3} />
                <Table.Summary.Cell index={5}>
                  <strong>{renderProductRevenueTotals(data as readonly EditableOrderProduct[], order.currency || 'PLN', true, i18n.language)}</strong>
                </Table.Summary.Cell>
                {canViewMargin && <Table.Summary.Cell index={6} />}
              </Table.Summary.Row>
            ) : undefined}
          />
        </div>
      )}

      {editingProducts && canEditProducts && (
        <Button
          type="dashed"
          onClick={handleAddProduct}
          icon={<PlusOutlined />}
          className="order-products-editor__add"
        >
          {t('orders:orderDetail.addProduct')}
        </Button>
      )}
    </Card>
  );
};
